import { memo, useEffect, useRef, useState } from "react";
import {
  BookmarkPlus,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  Gauge,
  ListChecks,
  Maximize2,
  Pause,
  Play,
  ScrollText,
  Settings2,
  Sparkles,
  Trash2,
  Volume2,
  AlertCircle,
  RotateCcw,
} from "lucide-react";
import { renderMarkdownToHtml } from "@/utils/markdownUtils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CitationsDisplay } from "@/components/CitationsDisplay";
import { ContextCitationsFooter } from "@/components/ContextCitationsFooter";
import { extractUsedCitations } from "@/lib/citationUtils";
import { messageContentToText } from "@/lib/chatHistoryLimits";
import type { SourceContext } from "@/services/contextOrchestrator";
import type { WebSearchCitation } from "@shared/schema";
import type { ChatErrorKind } from "@/lib/chatErrorCatalog";
import type { ChatMessageStatus } from "@/hooks/useChatSend";
import { voiceDisplayName, type TtsVoiceOption } from "@/services/ttsService";

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  citations?: WebSearchCitation[];
  contextCitations?: Record<number, SourceContext>;
  images?: Array<{ url: string; detail?: string }>;
  model?: string;
  autoSelected?: boolean;
  status?: ChatMessageStatus;
  errorMessage?: string;
  errorKind?: ChatErrorKind;
  receiptLine?: string;
};

interface MessageBubbleProps {
  message: Message;
  copiedId: string | null;
  savingToKnowledge: string | null;
  deletingMessageId: string | null;
  speakingMessageId: string | null;
  isSpeechPaused: boolean;
  availableVoices: SpeechSynthesisVoice[];
  selectedVoiceUri: string;
  speechRate: number;
  setSelectedVoiceUri: (value: string) => void;
  setSpeechRate: (value: number) => void;
  ttsVoices: TtsVoiceOption[];
  selectedTtsVoiceId: string;
  setSelectedTtsVoiceId: (value: string) => void;
  ttsLoadingMessageId: string | null;
  ttsErrorMessageId: string | null;
  ttsErrorMessage: string | null;
  onCopy: (content: string, id: string) => void;
  onSaveToKnowledge: (content: string, id: string) => void;
  onDelete: (id: string) => void;
  onFullscreenImage: (image: { url: string; alt: string }) => void;
  onDownloadImage: (url: string, filename: string) => void;
  onActionPlan: () => void;
  onSummarize: () => void;
  speak: (messageId: string, content: string) => void;
  pause: () => void;
  resume: () => void;
  /** When provided, clicking a cited document opens it in the right sidebar document creator */
  onOpenDocument?: (documentId: string) => void;
  onContinue?: (messageId: string) => void;
  onRetry?: (messageId: string) => void;
  showRetry?: boolean;
  isStreaming?: boolean;
}

function getModelDisplayName(modelId: string | undefined): string {
  if (!modelId) return "";
  if (modelId === "Deep Research") return modelId;
  const parts = modelId.replace(":online", "").split("/");
  const name = parts[parts.length - 1];
  return name
    .replace(/-/g, " ")
    .replace(/\b(gpt|claude|gemini|llama|mistral|deepseek|qwen)/gi, (m) => m.toUpperCase())
    .replace(/\b(\d+(\.\d+)?)\b/g, " $1")
    .replace(/\s+/g, " ")
    .trim();
}

const LONG_MESSAGE_THRESHOLD = 4000;
const COLLAPSED_PREVIEW_LINES = 10;

function buildCollapsedPreview(content: string): string {
  const lines = content.split("\n");
  if (lines.length <= COLLAPSED_PREVIEW_LINES) {
    return content.slice(0, LONG_MESSAGE_THRESHOLD) + "\n...";
  }
  return lines.slice(0, COLLAPSED_PREVIEW_LINES).join("\n") + "\n...";
}

const CollapsibleMessageContent = memo(function CollapsibleMessageContent({
  content,
  className,
  contextCitations,
}: {
  content: string | unknown;
  className?: string;
  contextCitations?: Record<number, SourceContext>;
}) {
  const textContent = messageContentToText(content);
  const isLong = textContent.length > LONG_MESSAGE_THRESHOLD;
  const [expanded, setExpanded] = useState(false);
  const displayContent = isLong && !expanded ? buildCollapsedPreview(textContent) : textContent;

  return (
    <div>
      <MessageContent content={displayContent} className={className} contextCitations={contextCitations} />
      {isLong && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2 h-auto px-0 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? (
            <>
              <ChevronUp className="mr-1 h-3 w-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 h-3 w-3" />
              Show full message ({textContent.length.toLocaleString()} chars)
            </>
          )}
        </Button>
      )}
    </div>
  );
});

const MessageContent = memo(function MessageContent({
  content,
  className,
  contextCitations,
}: {
  content: string;
  className?: string;
  contextCitations?: Record<number, SourceContext>;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      let html = renderMarkdownToHtml(content);

      if (contextCitations && Object.keys(contextCitations).length > 0) {
        html = html.replace(/\[(\d+)\]/g, (match, numStr) => {
          const num = parseInt(numStr, 10);
          const source = contextCitations[num];
          if (source) {
            const title = source.title || "Source";
            const escapedTitle = title.replace(/"/g, "&quot;");
            return `<span class="inline-citation" data-citation-index="${num}" data-citation-title="${escapedTitle}" tabindex="0" role="button">${match}</span>`;
          }
          return match;
        });
      }

      contentRef.current.innerHTML = html;
    }
  }, [content, contextCitations]);

  return <div ref={contentRef} className={className} style={{ userSelect: "text", WebkitUserSelect: "text" }} />;
});

export const MessageBubble = memo(function MessageBubble({
  message,
  copiedId,
  savingToKnowledge,
  deletingMessageId,
  speakingMessageId,
  isSpeechPaused,
  availableVoices,
  selectedVoiceUri,
  speechRate,
  setSelectedVoiceUri,
  setSpeechRate,
  ttsVoices,
  selectedTtsVoiceId,
  setSelectedTtsVoiceId,
  ttsLoadingMessageId,
  ttsErrorMessageId,
  ttsErrorMessage,
  onCopy,
  onSaveToKnowledge,
  onDelete,
  onFullscreenImage,
  onDownloadImage,
  onActionPlan,
  onSummarize,
  speak,
  pause,
    resume,
    onOpenDocument,
    onContinue,
    onRetry,
    showRetry = false,
    isStreaming = false,
  }: MessageBubbleProps) {
  const isIncompleteAssistant =
    message.role === "assistant" && (message.status === "partial" || message.status === "error");
  const showRetryButton = message.role === "user" && showRetry && onRetry && !isStreaming;

  return (
    <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
      <div className={`${message.role === "assistant" ? "max-w-full w-full" : "max-w-[75%]"}`}>
        {message.role === "assistant" ? (
          <div
            className={`rounded-lg p-3 bg-muted cursor-text select-text overflow-x-auto hover:ring-1 hover:ring-primary/20 transition-shadow ${
              isIncompleteAssistant ? "border border-destructive/40" : ""
            }`}
            data-testid={`message-${message.role}`}
            data-message-content
            title="Select text to copy or create document"
          >
            {isIncompleteAssistant && message.errorMessage && (
              <div
                className={`mb-3 flex flex-col gap-2 rounded-md border px-3 py-2 text-sm ${
                  message.status === "partial"
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100"
                    : "border-destructive/40 bg-destructive/10 text-destructive"
                }`}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">
                      {message.status === "partial" ? "Response interrupted" : "Response failed"}
                    </p>
                    <p className="text-xs opacity-90">{message.errorMessage}</p>
                  </div>
                </div>
                {message.status === "partial" && onContinue && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 w-fit text-xs"
                    onClick={() => onContinue(message.id)}
                    disabled={isStreaming}
                  >
                    Continue
                  </Button>
                )}
              </div>
            )}
            {(message.model || message.receiptLine) && (
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5 flex-wrap">
                {message.model && <Sparkles className="h-3 w-3" />}
                {message.model && <span>{getModelDisplayName(message.model)}</span>}
                {message.model && message.autoSelected && <span className="text-primary/70">(auto-selected)</span>}
                {message.receiptLine && <span className="opacity-70">{message.model ? "· " : ""}{message.receiptLine}</span>}
                <span className="opacity-60">·</span>
                <span className="opacity-70">
                  {message.timestamp.toLocaleDateString(undefined, { month: "short", day: "numeric" })}{" "}
                  {message.timestamp.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </span>
              </p>
            )}
            <CollapsibleMessageContent
              content={message.content}
              className="prose prose-sm dark:prose-invert max-w-none text-sm [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&>pre]:overflow-x-auto [&_code]:bg-muted-foreground/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_pre>code]:bg-transparent [&_pre>code]:p-0 [&_table]:overflow-x-auto [&_table]:block [&_.chart-container]:overflow-x-auto [&_svg]:max-w-none [&_.inline-citation]:inline-flex [&_.inline-citation]:items-center [&_.inline-citation]:px-1 [&_.inline-citation]:py-0.5 [&_.inline-citation]:mx-0.5 [&_.inline-citation]:text-xs [&_.inline-citation]:font-medium [&_.inline-citation]:rounded [&_.inline-citation]:bg-primary/10 [&_.inline-citation]:text-primary [&_.inline-citation]:cursor-pointer [&_.inline-citation:hover]:bg-primary/20"
              contextCitations={message.contextCitations}
            />
          </div>
        ) : (
          <div
            className="rounded-lg p-3 mb-1 bg-primary text-primary-foreground overflow-x-auto cursor-text select-text hover:ring-1 hover:ring-primary-foreground/20 transition-shadow"
            data-testid={`message-${message.role}`}
            data-message-content
            title="Select text to copy or create document"
          >
            <CollapsibleMessageContent
              content={message.content}
              className="prose prose-sm prose-invert max-w-none text-sm [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&>pre]:overflow-x-auto [&_table]:overflow-x-auto [&_table]:block"
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className="text-xs opacity-70">
                {message.timestamp.toLocaleDateString(undefined, { month: "short", day: "numeric" })}{" "}
                {message.timestamp.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              </p>
              {showRetryButton && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onRetry!(message.id)}
                >
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Retry
                </Button>
              )}
            </div>
          </div>
        )}

        {message.role === "assistant" && (
          <>
            {message.images && message.images.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.images.map((image, idx) => (
                  <div key={idx} className="rounded-lg overflow-hidden border relative group">
                    <img
                      src={image.url}
                      alt={`Generated image ${idx + 1}`}
                      className="w-full h-auto max-w-2xl cursor-pointer"
                      loading="lazy"
                      onDoubleClick={() =>
                        onFullscreenImage({ url: image.url, alt: `Generated image ${idx + 1}` })
                      }
                      title="Double-click to view fullscreen"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999'%3EImage Load Error%3C/text%3E%3C/svg%3E";
                      }}
                    />
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-8 w-8 bg-background/80 backdrop-blur-sm"
                        onClick={() =>
                          onFullscreenImage({ url: image.url, alt: `Generated image ${idx + 1}` })
                        }
                        data-testid={`button-fullscreen-image-${idx}`}
                        title="View fullscreen"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-8 w-8 bg-background/80 backdrop-blur-sm"
                        onClick={() =>
                          onDownloadImage(image.url, `generated-image-${Date.now()}.png`)
                        }
                        data-testid={`button-download-image-${idx}`}
                        title="Download image"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(() => {
              const actionButtons = (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (ttsLoadingMessageId === message.id) return;
                          const isCurrentlySpeaking = speakingMessageId === message.id;
                          if (isCurrentlySpeaking) {
                            if (isSpeechPaused) {
                              resume();
                            } else {
                              pause();
                            }
                          } else {
                            speak(message.id, message.content);
                          }
                        }}
                        className={
                          ttsErrorMessageId === message.id
                            ? "text-destructive hover:text-destructive"
                            : undefined
                        }
                        data-testid={`button-speak-${message.id}`}
                      >
                        {ttsLoadingMessageId === message.id ? (
                          <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : ttsErrorMessageId === message.id ? (
                          <AlertCircle className="h-4 w-4" />
                        ) : speakingMessageId === message.id ? (
                          isSpeechPaused ? (
                            <Play className="h-4 w-4" />
                          ) : (
                            <Pause className="h-4 w-4" />
                          )
                        ) : (
                          <Volume2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {ttsLoadingMessageId === message.id
                        ? "Generating audio..."
                        : ttsErrorMessageId === message.id
                          ? `${ttsErrorMessage ?? "Audio failed"} — click to retry`
                          : speakingMessageId === message.id
                            ? isSpeechPaused
                              ? "Resume"
                              : "Pause"
                            : "Read aloud"}
                    </TooltipContent>
                  </Tooltip>
                  {speakingMessageId === message.id && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-1.5 text-xs font-medium tabular-nums"
                          onClick={() => {
                            const speeds = [0.75, 1, 1.25, 1.5, 2];
                            const idx = speeds.indexOf(speechRate);
                            setSpeechRate(speeds[(idx + 1) % speeds.length] ?? 1);
                          }}
                          data-testid={`button-speed-cycle-${message.id}`}
                        >
                          {speechRate}×
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Playback speed — click to change</TooltipContent>
                    </Tooltip>
                  )}
                  {speakingMessageId === message.id && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`button-voice-settings-${message.id}`}>
                          <Settings2 className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64" align="start">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Voice</Label>
                            {ttsVoices.length > 0 ? (
                              <select
                                value={selectedTtsVoiceId || ttsVoices[0]?.voiceId || ""}
                                onChange={(e) => setSelectedTtsVoiceId(e.target.value)}
                                className="w-full p-2 text-sm rounded-md border bg-background"
                                data-testid="select-voice"
                              >
                                {ttsVoices.map((voice) => (
                                  <option key={voice.voiceId} value={voice.voiceId}>
                                    {voiceDisplayName(voice)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <select
                                value={selectedVoiceUri}
                                onChange={(e) => setSelectedVoiceUri(e.target.value)}
                                className="w-full p-2 text-sm rounded-md border bg-background"
                                data-testid="select-voice"
                              >
                                {availableVoices.map((voice) => (
                                  <option key={voice.voiceURI} value={voice.voiceURI}>
                                    {voice.name} ({voice.lang})
                                  </option>
                                ))}
                              </select>
                            )}
                            {ttsVoices.length > 0 && (
                              <p className="text-xs text-muted-foreground">
                                Voice changes apply the next time audio is played.
                              </p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Gauge className="h-4 w-4" />
                              Speed: {speechRate}x
                            </Label>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSpeechRate(Math.max(0.5, speechRate - 0.25))}
                                disabled={speechRate <= 0.5}
                                data-testid="button-speed-decrease"
                              >
                                -
                              </Button>
                              <div className="flex-1 flex justify-center gap-1">
                                {[0.75, 1, 1.25, 1.5, 2].map((rate) => (
                                  <Button
                                    key={rate}
                                    variant={speechRate === rate ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setSpeechRate(rate)}
                                    className="px-2"
                                    data-testid={`button-speed-${rate}`}
                                  >
                                    {rate}x
                                  </Button>
                                ))}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSpeechRate(Math.min(3, speechRate + 0.25))}
                                disabled={speechRate >= 3}
                                data-testid="button-speed-increase"
                              >
                                +
                              </Button>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onCopy(message.content, message.id)}
                        data-testid={`button-copy-${message.id}`}
                      >
                        {copiedId === message.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{copiedId === message.id ? "Copied!" : "Copy to clipboard"}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onSaveToKnowledge(message.content, message.id)}
                        disabled={savingToKnowledge === message.id}
                        data-testid={`button-save-knowledge-${message.id}`}
                      >
                        {savingToKnowledge === message.id ? (
                          <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <BookmarkPlus className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Save as document</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={onActionPlan} data-testid={`button-action-plan-${message.id}`}>
                        <ListChecks className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Convert to action plan</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={onSummarize} data-testid={`button-summarize-${message.id}`}>
                        <ScrollText className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Summarize &amp; archive</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(message.id)}
                        disabled={deletingMessageId === message.id}
                        className="text-destructive hover:text-destructive"
                        data-testid={`button-delete-${message.id}`}
                      >
                        {deletingMessageId === message.id ? (
                          <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete this message</TooltipContent>
                  </Tooltip>
                </>
              );

              const hasCitations = message.citations && message.citations.length > 0;
              const hasContextCitations =
                message.contextCitations && Object.keys(message.contextCitations).length > 0;

              if (hasCitations) {
                return (
                  <>
                    <CitationsDisplay citations={message.citations!} actionButtons={actionButtons} />
                    {hasContextCitations && (
                      <ContextCitationsFooter
                        sources={extractUsedCitations(message.content, message.contextCitations!)}
                        onOpenDocument={onOpenDocument}
                      />
                    )}
                  </>
                );
              }

              return (
                <>
                  {hasContextCitations && (
                    <ContextCitationsFooter
                      sources={extractUsedCitations(message.content, message.contextCitations!)}
                      onOpenDocument={onOpenDocument}
                    />
                  )}
                  <div className="flex items-center gap-1 mt-2 flex-wrap">{actionButtons}</div>
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
});
